import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { AppUser } from 'Src/views/App/App';
import { getSchoolSports } from 'Src/helpers/service/admin/user';
import { Sport } from 'Src/models/sport';
import { SchoolSettingsTabs } from '../SchoolSettingsTabs';
import { KIND } from 'Src/consts/school';
import './WebsiteWidgets.scss';

type WidgetType = 'fixtures' | 'results' | 'club-events' | 'news' | 'records' | 'house-points' | 'weekly-stats';
type UnionWidgetType = 'tournament-upcoming' | 'tournament-results' | 'tournament-calendar' | 'live-today';
const WIDGETS: [WidgetType, string, string][] = [
  ['fixtures', 'Upcoming fixtures', 'The next published, non-cancelled fixtures.'],
  ['results', 'Latest results', 'Recent published results with privacy rules applied.'],
  ['club-events', 'Upcoming clubs', 'The next published club sessions.'],
  ['news', 'Latest news', 'Recent stories published by your school.'],
  ['records', 'School records', 'The latest published sporting records.'],
  ['house-points', 'House points', 'Current academic-year house standings.'],
  ['weekly-stats', 'Weekly snapshot', 'Fixtures, clubs and pupil involvement this week.']
];
const UNION_WIDGETS: [UnionWidgetType, string, string][] = [
  ['tournament-upcoming', 'Upcoming tournaments', 'The next published tournaments from your union.'],
  ['tournament-results', 'Latest results', 'Recently completed published tournaments.'],
  ['tournament-calendar', 'Tournament calendar', 'A compact chronological tournament listing.'],
  ['live-today', 'Live today', 'Today’s tournaments with direct links for visitors.']
];
const publicOrigin = (domain: string) => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://${domain}.dev.squadintouch.com:3001`;
  }
  const parts = hostname.split('.');
  const suffix = parts.length > 1 ? parts.slice(1).join('.') : 'squadintouch.com';
  const isDev = parts.includes('dev');
  const protocol = isDev ? 'https:' : window.location.protocol;
  return `${protocol}//${domain}.${suffix}${isDev ? ':3001' : ''}`;
};

export const WebsiteWidgets = ({ user }: { user: AppUser }) => {
  const isUnion = user.activeSchool.kind === KIND.SCHOOL_UNION;
  const [type, setType] = useState<WidgetType | UnionWidgetType>(isUnion ? 'tournament-upcoming' : 'fixtures');
  const [theme, setTheme] = useState('light');
  const [limit, setLimit] = useState(5);
  const [accent, setAccent] = useState('#0878d1');
  const [sport, setSport] = useState('');
  const [sports, setSports] = useState<Sport[]>([]);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    getSchoolSports(user, { limit: 3000, order: 'name ASC' } as any).then(setSports).catch(() => setSports([]));
  }, [user.activeSchoolId]);
  const origin = publicOrigin(String(user.activeSchool.domain || ''));
  const widgets = isUnion ? UNION_WIDGETS : WIDGETS;
  const supportsSport = ['fixtures', 'results', 'club-events', 'records', 'tournament-upcoming', 'tournament-results', 'tournament-calendar', 'live-today'].includes(type);
  const sportParameter = supportsSport && sport ? `&sport=${sport}` : '';
  const query = `type=${type}&theme=${theme}&limit=${limit}&accent=${encodeURIComponent(accent)}${sportParameter}`;
  const code = useMemo(
    () =>
      `<script src="${origin}/embed.js" defer></script>\n<div data-sit-widget="${type}" data-theme="${theme}" data-limit="${limit}" data-accent="${accent}"${sportParameter ? ` data-sport="${sport}"` : ''}></div>`,
    [origin, type, theme, limit, accent, sport, sportParameter]
  );
  const copy = () =>
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
  return (
    <div className="schoolSettingsPage websiteWidgetsPage">
      <header className="schoolSettingsHeader">
        <h1>School settings</h1>
        <SchoolSettingsTabs isUnion={isUnion} />
      </header>
      <section className="websiteWidgetsHero">
        <span>Website embeds</span>
        <h2>Website widgets</h2>
        <p>
          Add live {isUnion ? 'tournaments' : 'school sport'} to your existing website. Widgets update automatically and follow your public visibility
          settings.
        </p>
      </section>
      <div className="websiteWidgetsLayout">
        <main>
          <section className="websiteWidgetsCard">
            <h3>Choose a widget</h3>
            <div className="websiteWidgetTypes">
              {widgets.map(item => (
                <button
                  type="button"
                  className={type === item[0] ? 'is-active' : ''}
                  onClick={() => setType(item[0])}
                  key={item[0]}
                >
                  <i />
                  <strong>{item[1]}</strong>
                  <span>{item[2]}</span>
                </button>
              ))}
            </div>
          </section>
          <section className="websiteWidgetsCard">
            <h3>Appearance</h3>
            <div className="websiteWidgetControls">
              <label>
                <span>Theme</span>
                <select value={theme} onChange={e => setTheme(e.target.value)}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </label>
              <label>
                <span>Items</span>
                <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
                  {[3, 4, 5, 6, 8, 10].map(value => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              {supportsSport && <label>
                <span>Sport</span>
                <select value={sport} onChange={e => setSport(e.target.value)}>
                  <option value="">All sports</option>
                  {sports.map(item => <option value={item.id} key={item.id}>{item.name}</option>)}
                </select>
              </label>}
              <label>
                <span>Accent colour</span>
                <div>
                  <input type="color" value={accent} onChange={e => setAccent(e.target.value)} />
                  <input
                    value={accent}
                    onChange={e => /^#[0-9a-f]{0,6}$/i.test(e.target.value) && setAccent(e.target.value)}
                  />
                </div>
              </label>
            </div>
          </section>
          <section className="websiteWidgetsCard">
            <div className="websiteWidgetCodeTitle">
              <div>
                <h3>Embed code</h3>
                <p>Paste this once where the widget should appear.</p>
              </div>
              <button type="button" onClick={copy}>
                {copied ? 'Copied' : 'Copy code'}
              </button>
            </div>
            <pre>{code}</pre>
            <small>The iframe is isolated from the host website and resizes automatically.</small>
          </section>
        </main>
        <aside>
          <div className="websiteWidgetPreviewTitle">
            <span>Live preview</span>
            <b>{widgets.find(item => item[0] === type)?.[1]}</b>
          </div>
          {user.activeSchool.domain ? (
            <iframe title="Widget preview" src={`${origin}/widget?${query}`} />
          ) : (
            <div className="websiteWidgetNoDomain">Set a public-site address to preview widgets.</div>
          )}
        </aside>
      </div>
    </div>
  );
};
